import { useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "./store/auth/authSlice";
import { fetchCmsMenus } from "./cms/services/cmsMenuService";
import AccessDenied from "./AccessDenied";

/* Module-level singleton — fetch once per browser session, share across all mounts */
let _cachedPrefixes = null;
let _fetchInProgress = null;

/** Build the list of all menu-controlled path prefixes from the API response */
const buildPrefixesFromMenus = (menus) => {
  const prefixes = [];
  for (const menu of menus) {
    if (menu.menu_link) prefixes.push(menu.menu_link);
    for (const sub of menu.sub_menus || []) {
      if (sub.sub_menu_link) prefixes.push(sub.sub_menu_link);
    }
  }
  return prefixes;
};

/**  path one that the CMS menu system controls */
const isMenuControlledRoute = (path, prefixes) =>
  prefixes.some(
    (prefix) => path === prefix || path.startsWith(prefix + "/")
  );

/** path accessible in the user's menu_access */
const isPathAccessible = (path, menuAccess, menuPrefixes) => {
  // If path exactly matches a registered menu/sub-menu prefix, it needs its own explicit access.
  // Don't let a parent menu's startsWith grant access to a separately-registered sub-menu.
  const isRegisteredPrefix = menuPrefixes.includes(path);

  for (const menu of menuAccess) {
    if (menu.menu_link) {
      if (path === menu.menu_link) return true;
      // Only use startsWith if path is not itself a registered CMS menu entry
      if (!isRegisteredPrefix && path.startsWith(menu.menu_link + "/")) return true;
    }
    for (const sub of menu.sub_menus || []) {
      if (sub.sub_menu_link) {
        if (
          path === sub.sub_menu_link ||
          path.startsWith(sub.sub_menu_link + "/")
        )
          return true;
      }
    }
  }
  return false;
};

const ProtectedRoute = () => {
  const { pathname } = useLocation();
  const [menuPrefixes, setMenuPrefixes] = useState(_cachedPrefixes);

  // Read menu_access from Redux state (login stores it there, not in localStorage)
  const authUser = useSelector(selectUser);
  const menuAccess = authUser?.menu_access;

  useEffect(() => {
    // Already cached — sync state if needed and bail out
    if (_cachedPrefixes !== null) {
      if (menuPrefixes === null) setMenuPrefixes(_cachedPrefixes);
      return;
    }

    // Kick off a single fetch; de-duplicate if multiple components mount at once
    if (!_fetchInProgress) {
      _fetchInProgress = fetchCmsMenus()
        .then((menus) => {
          _cachedPrefixes = buildPrefixesFromMenus(menus);
          return _cachedPrefixes;
        })
        .catch(() => {
          _cachedPrefixes = [];
          return _cachedPrefixes;
        });
    }

    _fetchInProgress.then((prefixes) => setMenuPrefixes(prefixes));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Still loading prefixes — render nothing briefly to avoid flash
  if (menuPrefixes === null) return null;

  // No menu_access in Redux → allow all (backward compat / superadmin with no restrictions)
  if (!Array.isArray(menuAccess) || menuAccess.length === 0) return <Outlet />;

  // Superadmin bypass — always allow access to all routes regardless of menu assignments
  const roleName = authUser?.cms_role?.role_name || authUser?.cms_role?.slug || "";
  if (/super/i.test(roleName)) return <Outlet />;

  // Not a menu-controlled route → always allow (utility pages, profile, 404, etc.)
  if (!isMenuControlledRoute(pathname, menuPrefixes)) return <Outlet />;

  // Menu-controlled route: check if user has access
  if (isPathAccessible(pathname, menuAccess, menuPrefixes)) return <Outlet />;

  // /cms/home with no access → redirect to first accessible menu
  if (pathname === "/cms/home" && Array.isArray(menuAccess)) {
    for (const menu of menuAccess) {
      if (menu.menu_link) return <Navigate to={menu.menu_link} replace />;
      const first = menu.sub_menus?.find(sm => sm.sub_menu_link);
      if (first) return <Navigate to={first.sub_menu_link} replace />;
    }
  }

  // No access → show access denied page
  return <AccessDenied />;
};

export default ProtectedRoute;
